import React, { useEffect, useRef, useState } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext.jsx';

const typeStyles = {
  success: 'bg-green-50 border-green-600 text-green-900',
  error: 'bg-red-50 border-red-600 text-red-900',
  info: 'bg-blue-50 border-blue-600 text-blue-900',
  warning: 'bg-yellow-50 border-yellow-600 text-yellow-900'
};

const typeIcons = {
  success: '✓',
  error: '✕',
  info: 'ℹ',
  warning: '⚠'
};

/**
 * ToastContainer
 * Renders all active toast notifications
 * Positioned at bottom-center on mobile, top-right on desktop
 */
export default function ToastContainer() {
  const { toasts, removeToast } = useNotifications();

  return (
    <div
      className="fixed z-[60] w-full md:w-auto md:max-w-sm space-y-2 p-4
                 bottom-0 left-0 md:top-4 md:right-4 md:left-auto md:bottom-auto"
      style={{ pointerEvents: 'none' }}
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onClose={() => removeToast(t.id)} />
      ))}
    </div>
  );
}

/**
 * ToastItem
 * Individual toast notification with auto-dismiss timer
 * Pauses on hover for improved UX
 */
function ToastItem({ toast, onClose }) {
  const {
    type = 'info',
    title,
    description,
    message, // Support both 'message' and 'description' for backwards compatibility
    duration = 8000,
    durationMs, // Support both 'duration' and 'durationMs'
    actionLabel,
    onAction,
    dismissible = true
  } = toast;

  // Use durationMs if provided, otherwise use duration
  const actualDuration = durationMs !== undefined ? durationMs : duration;

  const [hover, setHover] = useState(false);
  const start = useRef(Date.now());
  const remaining = useRef(actualDuration);

  // Auto-dismiss timer with pause on hover
  useEffect(() => {
    if (!actualDuration) return;

    const tick = () => {
      if (hover) {
        // Paused - recalculate start time to maintain remaining duration
        start.current = Date.now() - (actualDuration - remaining.current);
      } else {
        const elapsed = Date.now() - start.current;
        if (elapsed >= remaining.current) {
          onClose();
          return;
        }
        remaining.current = actualDuration - elapsed;
      }
      timeout.current = setTimeout(tick, 100);
    };

    let timeout = { current: null };
    timeout.current = setTimeout(tick, 100);

    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, [actualDuration, hover, onClose]);

  const role = type === 'error' || type === 'warning' ? 'alert' : 'status';
  const live = type === 'error' || type === 'warning' ? 'assertive' : 'polite';

  return (
    <div
      className={`border-l-4 rounded-md p-4 shadow-md ${typeStyles[type]} transition-all duration-200 animate-in slide-in-from-bottom-2 md:slide-in-from-right-2`}
      role={role}
      aria-live={live}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="flex items-start gap-3">
        <span className="text-xl font-bold flex-shrink-0" aria-hidden="true">
          {typeIcons[type]}
        </span>
        <div className="flex-1 min-w-0">
          {title && <p className="font-semibold text-base mb-1">{title}</p>}
          {(message || description) && <p className="text-sm">{message || description}</p>}
        </div>
        <div className="flex gap-2 flex-shrink-0 items-start">
          {actionLabel && onAction && (
            <button
              className="underline underline-offset-2 text-sm font-medium hover:no-underline transition-all"
              onClick={() => {
                onAction();
                onClose();
              }}
            >
              {actionLabel}
            </button>
          )}
          {dismissible && (
            <button
              aria-label="Fermer"
              className="text-lg font-bold hover:opacity-70 transition-opacity"
              onClick={onClose}
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
